import {
  useClimateMetrics,
  Metric,
  MetricDataFilters,
  MetricExportProfileMetricConfig,
  useClimateHazards,
  sortClimateHazards,
  MetricExportJobMetricKey,
  MetricExportJobSummary,
  useMetricExportJobs,
} from '../hooks/analytics'
import { useLocationGroups, useLocations } from '../hooks/locations'
import { api } from 'balkerne-core/api'
import { useSnackbar } from 'notistack'
import { MouseEvent as ReactMouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ago } from '../core/time'
import {
  CanonicalPeriod,
  EmissionTier,
  PERIODS as canonicalPeriodOptions,
  emissionTierOptions,
} from '../components/climateAnalytics/common'
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Pagination,
  Popover,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined'
import Page from '../components/Page'
import MetricExportProfilePanel, {
  MetricExportProfileCompatibility,
  MetricExportProfilePanelHandle,
} from '../components/climateAnalytics/export/MetricExportProfilePanel'
import HazardFilterDialog from '../components/climateAnalytics/export/HazardFilterDialog'
import {
  cloneMetricDataFilters,
  hasMetricDataFilters,
  sanitizeMetricDataFilters,
} from '../components/climateAnalytics/export/metricDataFilters'
import {
  buildEmptyMetricSelection,
  buildMetricExportConfigs,
  coerceDefaultsForFacetLists,
  HazardFiltersByHazard,
  MetricFacetScalarValue,
  MetricFacetValues,
  MetricSelectionState,
  toBooleanList,
  toStringList,
} from '../components/climateAnalytics/export/metricExportState'
import type { HazardInfo } from '../types/climate'

// -------------------- Types -------------------- //

interface ExportMetricsParams {
  property_ids?: number[]
  group_id?: number
  metrics: MetricExportProfileMetricConfig[]
  emission_tiers?: EmissionTier[] | null
  canonical_periods?: CanonicalPeriod[] | null
  adapted?: boolean | null
  effective?: boolean
  layout: ExportLayoutConfig
}

type ExportLayoutMode = 'raw_only' | 'wide_only' | 'raw_and_wide'
type ExportSheetPartitioning = 'none' | 'hazard' | 'group' | 'hazard_group'

interface ExportLayoutConfig {
  mode: ExportLayoutMode
  sheet_partitioning: ExportSheetPartitioning
  short_metric_names: boolean
  compact_headers: boolean
  include_column_dictionary: boolean
}

const emissionTierExportMeta: Record<
  EmissionTier,
  {
    title: string
    detail: string
    scenarios: string
  }
> = {
  [EmissionTier.HIGH]: {
    title: 'High emissions',
    detail: '+4°C',
    scenarios: 'Underlying scenarios: SSP5 8.5, RCP 8.5',
  },
  [EmissionTier.MODERATE]: {
    title: 'Moderate emissions',
    detail: '+2 - 4°C',
    scenarios: 'Underlying scenarios: SSP2 4.5, RCP 4.5',
  },
  [EmissionTier.LOW]: {
    title: 'Low emissions',
    detail: '+1.5 - 2°C',
    scenarios: 'Underlying scenarios: SSP1 2.6, RCP 2.6',
  },
}

const canonicalPeriodMeta: Record<
  CanonicalPeriod,
  {
    title: string
    detail: string
    description: string
  }
> = {
  [CanonicalPeriod.SHORT_TERM]: {
    title: 'Short Term',
    detail: '2030',
    description: 'Near-term horizon for early transition and exposure planning.',
  },
  [CanonicalPeriod.MID_TERM]: {
    title: 'Mid Term',
    detail: '2050',
    description: 'Medium-range horizon for strategic adaptation planning.',
  },
  [CanonicalPeriod.LONG_TERM]: {
    title: 'Long Term',
    detail: '2100',
    description: 'Long-range horizon for end-of-century climate exposure.',
  },
}

const exportLayoutModeOptions: { value: ExportLayoutMode; label: string; description: string }[] = [
  {
    value: 'raw_and_wide',
    label: 'Raw + analysis',
    description: 'Include both the row-based raw sheet and the analysis-friendly wide sheets.',
  },
  {
    value: 'raw_only',
    label: 'Raw only',
    description: 'Export only the row-based raw sheet.',
  },
  {
    value: 'wide_only',
    label: 'Analysis only',
    description: 'Export only the wide analysis sheet layout.',
  },
]

const exportSheetPartitioningOptions: {
  value: ExportSheetPartitioning
  label: string
  description: string
}[] = [
  {
    value: 'none',
    label: 'Single analysis sheet',
    description: 'Keep all wide export columns together in one analysis sheet.',
  },
  {
    value: 'hazard',
    label: 'Split by hazard',
    description: 'Create one analysis sheet per hazard, such as Flood or Wildfire.',
  },
  {
    value: 'group',
    label: 'Split by group',
    description: 'Create one analysis sheet per metric group, such as Score or Financial.',
  },
  {
    value: 'hazard_group',
    label: 'Split by hazard and group',
    description: 'Create the most compact sheet set, such as Flood - Financial.',
  },
]

const defaultExportLayout: ExportLayoutConfig = {
  mode: 'wide_only',
  sheet_partitioning: 'hazard_group',
  short_metric_names: true,
  compact_headers: true,
  include_column_dictionary: true,
}

type FacetScalarValue = MetricFacetScalarValue
type FacetValues = MetricFacetValues
type MetricSelection = MetricSelectionState

type HazardMetricGroup = {
  id: number
  label: string
  order: number
  metrics: Metric[]
}

type FacetDisplayRow = {
  key: string
  label: string
  values: string[]
}

const facetLabelByKey: Record<string, string> = {
  statistic: 'Statistic',
  variable: 'Variable',
  threshold: 'Threshold',
  target: 'Target',
  source: 'Source',
  defended: 'Defense',
  horizon: 'Horizon',
  member: 'Member',
}

function titleize(text: string): string {
  return text
    .replace(/[_-]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

function presentFacetValue(value: string): string {
  const normalized = value.replace(/[_-]+/g, ' ').trim()
  if (!normalized) return value
  return normalized === normalized.toLowerCase() ? titleize(normalized) : normalized
}

function toMetricKeyRef(metricKey: MetricExportJobMetricKey): string {
  return `${metricKey.hazard}:${metricKey.code}`
}

type MetricKeyDisplayRow = {
  key: string
  hazardLabel: string
  metricName: string
}

function buildMetricKeyDisplayRows(
  metricKeys: MetricExportJobMetricKey[],
  metricNameByRef: Map<string, string>,
  hazardLabelByHazard: Map<string, string>,
): MetricKeyDisplayRow[] {
  if (!metricKeys.length) {
    return []
  }

  const seen = new Set<string>()
  const rows: MetricKeyDisplayRow[] = []

  metricKeys.forEach(metricKey => {
    const key = toMetricKeyRef(metricKey)
    if (seen.has(key)) {
      return
    }

    seen.add(key)
    rows.push({
      key,
      hazardLabel: hazardLabelByHazard.get(metricKey.hazard) ?? metricKey.hazard,
      metricName: metricNameByRef.get(key) ?? 'Unknown metric',
    })
  })

  return rows
}

function toFacetDisplayRows(metric: Metric, values: FacetValues): FacetDisplayRow[] {
  const schema = metric.facet_schema || {}
  const rows: FacetDisplayRow[] = []

  for (const [key, def] of Object.entries(schema)) {
    const raw = values[key]
    if (raw === undefined) continue

    if (def.type === 'bool') {
      const bools = toBooleanList(raw)
      if (!bools.length) continue
      rows.push({
        key,
        label: facetLabelByKey[key] ?? titleize(key),
        values: bools.map(v => {
          if (key === 'defended') return v ? 'Defended' : 'Undefended'
          return v ? 'True' : 'False'
        }),
      })
      continue
    }

    const strings = toStringList(raw)
    if (!strings.length) continue
    rows.push({
      key,
      label: facetLabelByKey[key] ?? titleize(key),
      values: strings.map(presentFacetValue),
    })
  }

  return rows
}

function FacetDialog({
  open,
  metric,
  initialValues,
  onCancel,
  onSave,
  onClear,
}: {
  open: boolean
  metric: Metric
  initialValues: FacetValues
  onCancel: () => void
  onSave: (nextValues: FacetValues) => void // caller sets useDefaults=false on save
  onClear: () => void // caller sets useDefaults=true and clears values
}) {
  const [values, setValues] = useState<FacetValues>({})

  useEffect(() => {
    setValues(initialValues)
  }, [initialValues, open])

  const schema = metric.facet_schema || {}

  const handleField = (key: string, value: FacetScalarValue[]) => setValues(v => ({ ...v, [key]: value }))

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Configure filters</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {Object.entries(schema).length === 0 && (
            <Alert severity="info">This metric has no configurable facets.</Alert>
          )}
          {Object.entries(schema).map(([key, def]) => {
            const raw = values[key]
            if (def.type === 'enum') {
              const arr = toStringList(raw)
              return (
                <FormControl key={key} fullWidth>
                  <InputLabel id={`${metric.id}-${key}-label`}>{key}</InputLabel>
                  <Select
                    multiple
                    labelId={`${metric.id}-${key}-label`}
                    label={key}
                    value={arr}
                    onChange={e => handleField(key, toStringList(e.target.value))}
                    renderValue={selected => (
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        {(selected as string[]).map(opt => (
                          <Chip key={opt} size="small" label={opt} sx={{ mr: 0.5, mb: 0.5 }} />
                        ))}
                      </Stack>
                    )}
                  >
                    {(def.values ?? []).map(opt => (
                      <MenuItem key={opt} value={opt}>
                        <Checkbox checked={arr.includes(opt)} />
                        <Typography sx={{ ml: 1 }}>{opt}</Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )
            }
            if (def.type === 'bool') {
              const arr = toBooleanList(raw)
              const toggle = (boolValue: boolean, checked: boolean) => {
                if (checked) handleField(key, Array.from(new Set([...arr, boolValue])))
                else
                  handleField(
                    key,
                    arr.filter(v => v !== boolValue),
                  )
              }
              return (
                <FormControl key={key}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    {key}
                  </Typography>
                  <FormGroup row>
                    <FormControlLabel
                      control={
                        <Checkbox checked={arr.includes(true)} onChange={(_, checked) => toggle(true, checked)} />
                      }
                      label="True"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox checked={arr.includes(false)} onChange={(_, checked) => toggle(false, checked)} />
                      }
                      label="False"
                    />
                  </FormGroup>
                </FormControl>
              )
            }
            // text
            return (
              <Autocomplete<string, true, false, true>
                key={key}
                multiple
                freeSolo
                fullWidth
                options={[]}
                value={toStringList(raw)}
                onChange={(_, next) => handleField(key, toStringList(next))}
                renderTags={(tagValue, getTagProps) =>
                  tagValue.map((option, index) => (
                    <Chip size="small" label={option} {...getTagProps({ index })} key={`${option}-${index}`} />
                  ))
                }
                renderInput={params => <TextField {...params} label={key} placeholder="Type a value and press Enter" />}
              />
            )
          })}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClear} color="inherit">
          Reset to default
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onCancel}>Cancel</Button>
        <Button variant="contained" onClick={() => onSave(values)}>
          Save custom
        </Button>
      </DialogActions>
    </Dialog>
  )
}

type FilterConfigButtonProps = {
  active?: boolean
  title: string
  ariaLabel: string
  onClick: (event: ReactMouseEvent<HTMLButtonElement>) => void
}

function FilterConfigButton({ active = false, title, ariaLabel, onClick }: FilterConfigButtonProps) {
  return (
    <Tooltip title={title}>
      <IconButton
        size="small"
        aria-label={ariaLabel}
        sx={{
          width: 28,
          height: 28,
          color: active ? 'primary.main' : 'text.secondary',
          border: '1px solid',
          borderColor: active ? 'primary.main' : 'divider',
          backgroundColor: active ? 'action.selected' : 'transparent',
          '&:hover': {
            backgroundColor: active ? 'action.selected' : 'action.hover',
          },
        }}
        onClick={onClick}
      >
        <TuneOutlinedIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Tooltip>
  )
}

function MetricRow({
  metric,
  hazardLabel,
  selection,
  onChange,
  isProfileIncompatible = false,
  onResetProfileMetric,
}: {
  metric: Metric
  hazardLabel: string
  selection: MetricSelection
  onChange: (next: MetricSelection) => void
  isProfileIncompatible?: boolean
  onResetProfileMetric?: () => void
}) {
  const [open, setOpen] = useState(false)
  const defaultFacetValues = useMemo(() => coerceDefaultsForFacetLists(metric), [metric])
  const hasConfigurableFacets = useMemo(() => Object.keys(metric.facet_schema || {}).length > 0, [metric])
  const facetDetails = useMemo(
    () => toFacetDisplayRows(metric, selection.useDefaults ? defaultFacetValues : selection.facetValues),
    [metric, selection.useDefaults, selection.facetValues, defaultFacetValues],
  )
  const facetOverrideDetails = useMemo(
    () => (!selection.useDefaults ? toFacetDisplayRows(metric, selection.facetValues) : []),
    [metric, selection.useDefaults, selection.facetValues],
  )
  const hasFacetOverrides = facetOverrideDetails.length > 0

  const handleOpen = () => {
    setOpen(true)
  }

  const handleSave = (vals: FacetValues) => {
    onChange({ ...selection, useDefaults: false, facetValues: vals })
    setOpen(false)
  }

  const handleClear = () => {
    // revert to defaults mode; clear facet values so payload won't include overrides
    onChange({ ...selection, useDefaults: true, facetValues: {} })
    setOpen(false)
  }

  const metricInfoContent = (
    <Box sx={{ maxWidth: 420 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        {hazardLabel} - {metric.label}
      </Typography>
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        {metric.unit ? <Chip size="small" variant="outlined" label={`${metric.unit}`} /> : null}
        {metric.uses_return_periods ? <Chip size="small" variant="outlined" label="Return Periods" /> : null}
      </Stack>
      {metric.description ? (
        <Box
          sx={{
            ml: 1,
            mb: 1,
            p: 1,
            borderRadius: 1,
            backgroundColor: 'grey.100',
          }}
        >
          <Typography variant="body2" color="text.primary">
            {metric.description}
          </Typography>
        </Box>
      ) : null}
      {facetDetails.length > 0 ? (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
            {selection.useDefaults ? 'Facet defaults' : 'Facet overrides'}
          </Typography>
          <Stack spacing={0.75} sx={{ mt: 0.5 }}>
            {facetDetails.map(row => (
              <Stack key={`${metric.id}-${row.key}`} direction="row" spacing={0.75} alignItems="center">
                <Typography variant="caption" color="text.secondary">
                  {row.label}:
                </Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {row.values.map(value => (
                    <Chip key={`${metric.id}-${row.key}-${value}`} size="small" variant="outlined" label={value} />
                  ))}
                </Stack>
              </Stack>
            ))}
          </Stack>
        </>
      ) : null}
    </Box>
  )

  return (
    <Box
      key={metric.id}
      sx={{
        display: 'flex',
        alignItems: 'center',
        py: 0.85,
        px: 1.1,
        minHeight: 52,
        borderBottom: '1px solid',
        borderColor: 'divider',
        borderLeft: '3px solid',
        borderLeftColor: isProfileIncompatible
          ? 'error.main'
          : selection.selected
          ? 'primary.main'
          : metric.highlight
          ? 'primary.light'
          : 'transparent',
        backgroundColor: isProfileIncompatible
          ? 'rgba(211, 47, 47, 0.05)'
          : selection.selected
          ? 'action.selected'
          : metric.highlight
          ? 'action.hover'
          : 'transparent',
        '&:hover': {
          backgroundColor: isProfileIncompatible
            ? 'rgba(211, 47, 47, 0.08)'
            : selection.selected
            ? 'action.selected'
            : 'action.hover',
        },
      }}
    >
      <Checkbox
        checked={selection.selected}
        onChange={e => onChange({ ...selection, selected: e.target.checked })}
        size="small"
        sx={{ mr: 0.8 }}
      />

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ minWidth: 0 }}>
          <Typography variant="body1">{metric.label}</Typography>
          <Tooltip
            title={metricInfoContent}
            placement="right"
            enterDelay={120}
            leaveDelay={100}
            slotProps={{
              popper: {
                sx: {
                  zIndex: 1600,
                },
              },
              tooltip: {
                sx: {
                  maxWidth: 460,
                  p: 1.25,
                  backgroundColor: 'common.white',
                  color: 'text.primary',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  boxShadow: '0 8px 24px rgba(15, 23, 42, 0.14)',
                },
              },
            }}
          >
            <IconButton
              size="small"
              aria-label={`Metric information for ${metric.label}`}
              sx={{ p: 0.25, color: 'text.secondary' }}
            >
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {metric.unit ? (
            <Typography component="span" variant="body2" color="text.secondary">
              {metric.unit}
            </Typography>
          ) : null}
          {metric.uses_return_periods ? <Chip size="small" variant="outlined" label="Return Periods" /> : null}
        </Stack>
      </Box>

      {facetOverrideDetails.length > 0 && (
        <Stack direction="row" spacing={0.5} sx={{ mr: 1, justifyContent: 'flex-end', flexWrap: 'nowrap' }}>
          {facetOverrideDetails.map(row => (
            <Chip
              key={`${metric.id}-inline-${row.key}`}
              size="small"
              variant="outlined"
              label={`${row.label}: ${row.values.join(', ')}`}
            />
          ))}
        </Stack>
      )}

      {isProfileIncompatible ? (
        <Chip size="small" color="error" variant="outlined" label="Incompatible" sx={{ mr: 0.75, fontWeight: 600 }} />
      ) : null}

      {isProfileIncompatible && onResetProfileMetric ? (
        <Button
          size="small"
          color="error"
          variant="text"
          sx={{ minWidth: 'auto', mr: 0.75 }}
          onClick={onResetProfileMetric}
        >
          Reset
        </Button>
      ) : null}

      {hasConfigurableFacets ? (
        <Box sx={{ mr: 0.75 }}>
          <FilterConfigButton
            active={hasFacetOverrides}
            title={hasFacetOverrides ? 'Edit facet filters' : 'Configure facet filters'}
            ariaLabel={`Configure facets for ${metric.label}`}
            onClick={() => handleOpen()}
          />
        </Box>
      ) : null}

      {hasConfigurableFacets ? (
        <FacetDialog
          open={open}
          metric={metric}
          initialValues={selection.useDefaults ? defaultFacetValues : selection.facetValues}
          onCancel={() => setOpen(false)}
          onSave={handleSave}
          onClear={handleClear}
        />
      ) : null}
    </Box>
  )
}

function MetricsExportPage() {
  const { enqueueSnackbar } = useSnackbar()

  const { data: hazards, isLoading: hzLoading, error: hzError } = useClimateHazards()
  const { data: metrics, isLoading: mLoading, error: mError } = useClimateMetrics()
  const { data: locationGroups, isLoading: groupsLoading } = useLocationGroups()
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null)
  const { data: locations, isLoading: locLoading } = useLocations(selectedGroupId ? { group_id: selectedGroupId } : {})
  const metricExportJobsQuery = useMetricExportJobs()
  const metricExportProfilePanelRef = useRef<MetricExportProfilePanelHandle>(null)

  const [exportAllProps, setExportAllProps] = useState<boolean>(true) // default ON
  const [selectedPropertyIds, setSelectedPropertyIds] = useState<number[]>([])
  // Ref-based filter: updated synchronously in onInputChange so MUI's internal
  // resets (blur, polling-triggered value changes) can never race it.
  const propertySearchRef = useRef<string>('')
  const filterPropertyOptions = useCallback((options: any[], _state: any) => {
    const text = propertySearchRef.current.toLowerCase().trim()
    if (!text) return options
    return options.filter((o: any) => o.name.toLowerCase().includes(text))
  }, [])
  const [tiers, setTiers] = useState<EmissionTier[]>([EmissionTier.LOW, EmissionTier.MODERATE, EmissionTier.HIGH])
  const [allPeriods, setAllPeriods] = useState<boolean>(false)
  const [canonicalPeriods, setCanonicalPeriods] = useState<CanonicalPeriod[]>([...canonicalPeriodOptions])
  const [layout, setLayout] = useState<ExportLayoutConfig>(defaultExportLayout)
  const [layoutOptionsAnchorEl, setLayoutOptionsAnchorEl] = useState<HTMLElement | null>(null)
  const [submitState, setSubmitState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle')
  const [submitError, setSubmitError] = useState<string>('')
  const [exportsOpen, setExportsOpen] = useState<boolean>(false)
  const [exportsPage, setExportsPage] = useState<number>(1)
  const [incompatibleProfileMetricIds, setIncompatibleProfileMetricIds] = useState<Set<number>>(new Set())
  const [incompatibleMetricCountByHazard, setIncompatibleMetricCountByHazard] = useState<Map<string, number>>(new Map())
  const [hazardDescriptionDialog, setHazardDescriptionDialog] = useState<{
    title: string
    description: string
  } | null>(null)
  const [hazardFilterDialog, setHazardFilterDialog] = useState<{
    hazardKey: string
    hazardLabel: string
  } | null>(null)

  const hazardItems = useMemo(() => hazards ?? [], [hazards])
  const metricItems = useMemo(() => metrics ?? [], [metrics])
  const locationGroupItems = useMemo(() => locationGroups ?? [], [locationGroups])
  const locationItems = useMemo(() => locations ?? [], [locations])
  const selectedLocationItems = useMemo(
    () => locationItems.filter((property: any) => selectedPropertyIds.includes(property.id)),
    [locationItems, selectedPropertyIds],
  )

  // metricId -> selection
  const [selections, setSelections] = useState<Record<number, MetricSelection>>({})
  const [hazardFiltersByHazard, setHazardFiltersByHazard] = useState<HazardFiltersByHazard>({})

  // Initialize selections when metrics arrive
  useEffect(() => {
    if (metricItems.length === 0) return
    setSelections(prev => {
      const next: Record<number, MetricSelection> = { ...prev }
      metricItems.forEach(m => {
        if (!next[m.id]) {
          next[m.id] = buildEmptyMetricSelection(m.id)
        }
      })
      return next
    })
  }, [metricItems])

  useEffect(() => {
    setSelectedPropertyIds(prev => prev.filter(id => locationItems.some((location: any) => location.id === id)))
  }, [locationItems])

  const hazardLookup = useMemo(() => {
    const map = new Map<string, HazardInfo>()
    hazardItems.forEach(h => map.set(h.name, h))
    return map
  }, [hazardItems])
  const hazardLabelByHazard = useMemo(() => {
    const map = new Map<string, string>()
    hazardItems.forEach(h => map.set(h.name, h.display_name))
    return map
  }, [hazardItems])

  const metricsByHazardGroup = useMemo(() => {
    const byHazard = new Map<string, Map<number, HazardMetricGroup>>()

    metricItems.forEach(metric => {
      if (metric.hide) {
        return
      }

      const metricGroup = metric.group
      const hazardGroups = byHazard.get(metric.hazard) || new Map<number, HazardMetricGroup>()

      if (!hazardGroups.has(metricGroup.id)) {
        hazardGroups.set(metricGroup.id, {
          id: metricGroup.id,
          label: metricGroup.label,
          order: metricGroup.order,
          metrics: [],
        })
      }

      hazardGroups.get(metricGroup.id)?.metrics.push(metric)
      byHazard.set(metric.hazard, hazardGroups)
    })

    const orderedByHazard = new Map<string, HazardMetricGroup[]>()
    for (const [hazard, groupsById] of byHazard.entries()) {
      const orderedGroups = Array.from(groupsById.values())
      orderedGroups.forEach(group => {
        group.metrics.sort((left, right) => {
          if (left.order !== right.order) {
            return left.order - right.order
          }
          return left.label.localeCompare(right.label)
        })
      })

      orderedGroups.sort((left, right) => {
        if (left.order !== right.order) {
          return left.order - right.order
        }
        return left.label.localeCompare(right.label)
      })

      orderedByHazard.set(hazard, orderedGroups)
    }

    return orderedByHazard
  }, [metricItems])

  const orderedHazardGroups = useMemo(() => {
    const knownHazards = sortClimateHazards(
      Array.from(
        new Map(
          Array.from(metricsByHazardGroup.keys())
            .map(hazardKey => hazardLookup.get(hazardKey))
            .filter((hazard): hazard is HazardInfo => hazard != null)
            .map(hazard => [hazard.name, hazard]),
        ).values(),
      ),
    )
    const hazardIndexByName = new Map(knownHazards.map((hazard, index) => [hazard.name, index]))

    return Array.from(metricsByHazardGroup.entries()).sort(([leftKey], [rightKey]) => {
      const leftIndex = hazardIndexByName.get(leftKey)
      const rightIndex = hazardIndexByName.get(rightKey)

      if (leftIndex != null && rightIndex != null) {
        return leftIndex - rightIndex
      }
      if (leftIndex != null) {
        return -1
      }
      if (rightIndex != null) {
        return 1
      }
      return leftKey.localeCompare(rightKey)
    })
  }, [hazardLookup, metricsByHazardGroup])

  const selectedMetrics: Metric[] = useMemo(() => {
    return metricItems.filter(metric => !metric.hide && selections[metric.id]?.selected)
  }, [metricItems, selections])
  const selectedMetricCountByHazard = useMemo(() => {
    const counts = new Map<string, number>()
    selectedMetrics.forEach(metric => {
      counts.set(metric.hazard, (counts.get(metric.hazard) ?? 0) + 1)
    })
    return counts
  }, [selectedMetrics])
  const selectedMetricCount = selectedMetrics.length
  const includesWideSheets = layout.mode !== 'raw_only'
  const layoutOptionsOpen = Boolean(layoutOptionsAnchorEl)
  const layoutOptionsSummary = [
    layout.short_metric_names ? 'Short names on' : 'Short names off',
    layout.compact_headers ? 'Compact headers on' : 'Compact headers off',
    layout.include_column_dictionary ? 'Column dictionary on' : 'Column dictionary off',
  ].join(' • ')
  const selectedPropertyCount = useMemo(
    () => (exportAllProps ? locationItems.length : selectedPropertyIds.length),
    [exportAllProps, locationItems, selectedPropertyIds],
  )
  const exportDisabled = submitState === 'submitting' || selectedMetricCount === 0

  const exportJobs = metricExportJobsQuery.data ?? []
  const exportCount = exportJobs.length
  const hasActiveExportJobs = exportJobs.some(j => j.status === 'QUEUED' || j.status === 'RUNNING')

  const EXPORTS_PAGE_SIZE = 5
  const exportsPageCount = Math.max(1, Math.ceil(exportCount / EXPORTS_PAGE_SIZE))
  const exportsPageSafe = Math.min(exportsPage, exportsPageCount)
  const exportsPageJobs = exportJobs.slice(
    (exportsPageSafe - 1) * EXPORTS_PAGE_SIZE,
    exportsPageSafe * EXPORTS_PAGE_SIZE,
  )

  useEffect(() => {
    if (hasActiveExportJobs) {
      setExportsOpen(true)
      setExportsPage(1)
    }
  }, [hasActiveExportJobs])

  useEffect(() => {
    setExportsPage(prev => Math.min(prev, exportsPageCount))
  }, [exportsPageCount])

  useEffect(() => {
    if (!includesWideSheets) {
      setLayoutOptionsAnchorEl(null)
    }
  }, [includesWideSheets])

  const handleSelectionChange = (metricId: number, next: MetricSelection) => {
    setSelections(s => ({ ...s, [metricId]: next }))
  }

  const handleProfileCompatibilityChange = useCallback((compatibility: MetricExportProfileCompatibility) => {
    setIncompatibleProfileMetricIds(compatibility.incompatibleMetricIds)
    setIncompatibleMetricCountByHazard(compatibility.incompatibleCountByHazard)
  }, [])

  const handleHazardFilterSave = useCallback((hazardKey: string, filters: MetricDataFilters | null) => {
    const cleaned = sanitizeMetricDataFilters(filters) ?? null
    setHazardFiltersByHazard(prev => {
      const next = { ...prev }
      if (cleaned) {
        next[hazardKey] = cloneMetricDataFilters(cleaned) as MetricDataFilters
      } else {
        delete next[hazardKey]
      }
      return next
    })
  }, [])

  const formatBytes = (bytes?: number | null) => {
    if (!bytes) return null
    const mb = bytes / (1024 * 1024)
    if (mb < 1024) return `${mb.toFixed(1)} MB`
    return `${(mb / 1024).toFixed(2)} GB`
  }

  const statusChip = (status: MetricExportJobSummary['status']) => {
    if (status === 'COMPLETE') return { label: 'Complete', color: 'success' as const, variant: 'filled' as const }
    if (status === 'FAILED') return { label: 'Failed', color: 'error' as const, variant: 'filled' as const }
    if (status === 'RUNNING') return { label: 'Processing', color: 'warning' as const, variant: 'outlined' as const }
    return { label: 'Queued', color: 'default' as const, variant: 'outlined' as const }
  }

  const handleDownload = async (jobId: string) => {
    try {
      const res = await api.get(`/analytics/cc/metrics/exports/${jobId}/download`, {
        params: { redirect: false },
      })
      const url = res?.data?.url
      if (!url) {
        enqueueSnackbar('Download URL not available yet.', { variant: 'warning' })
        return
      }
      window.location.assign(url)
    } catch (err: any) {
      const status = err?.response?.status
      if (status === 409) enqueueSnackbar('Export is not ready yet.', { variant: 'info' })
      else if (status === 410) enqueueSnackbar('Export has expired.', { variant: 'warning' })
      else enqueueSnackbar('Failed to start download.', { variant: 'error' })
    }
  }

  const handleExport = async () => {
    const allPropsIds = locationItems.map((property: any) => property.id)
    const propertyIdsForPayload = exportAllProps ? allPropsIds : selectedPropertyIds

    if (!propertyIdsForPayload.length) {
      setSubmitError('No properties available to export.')
      setSubmitState('error')
      return
    }
    if (!selectedMetrics.length) {
      setSubmitError('Please select at least one metric to export.')
      setSubmitState('error')
      return
    }
    if (!allPeriods && canonicalPeriods.length === 0) {
      setSubmitError('Select at least one canonical period or enable all periods.')
      setSubmitState('error')
      return
    }

    const metricsConfig = buildMetricExportConfigs({
      metrics: metricItems,
      selections,
      hazardFiltersByHazard,
    })

    const payload: ExportMetricsParams = {
      ...(exportAllProps && selectedGroupId ? { group_id: selectedGroupId } : { property_ids: propertyIdsForPayload }),
      metrics: metricsConfig,
      emission_tiers: tiers.length ? tiers : undefined,
      canonical_periods: allPeriods ? undefined : canonicalPeriods,
      adapted: false,
      effective: false,
      layout,
    }

    try {
      setSubmitState('submitting')
      setSubmitError('')
      await api.post('/analytics/cc/metrics/export', payload)
      setSubmitState('success')
      enqueueSnackbar('Export queued. It will appear under “Recent exports”.', { variant: 'success' })
      metricExportJobsQuery.refetch()
    } catch (err: any) {
      setSubmitError(err?.response?.data?.detail || err?.message || 'Export failed')
      setSubmitState('error')
      enqueueSnackbar('Export failed. Please try again.', { variant: 'error' })
    }
  }
  const toggleTier = (tier: EmissionTier) =>
    setTiers(prev => (prev.includes(tier) ? prev.filter(t => t !== tier) : [...prev, tier]))
  const toggleCanonicalPeriod = (period: CanonicalPeriod) =>
    setCanonicalPeriods(prev => (prev.includes(period) ? prev.filter(p => p !== period) : [...prev, period]))
  const scopeLoading = locLoading || groupsLoading
  const metricsLoading = hzLoading || mLoading

  const selectedGroup = useMemo(
    () => locationGroupItems.find((group: any) => group.id === selectedGroupId) ?? null,
    [locationGroupItems, selectedGroupId],
  )
  const metricNameByRef = useMemo(() => {
    const out = new Map<string, string>()
    metricItems.forEach(metric => out.set(`${metric.hazard}:${metric.code}`, metric.label))
    return out
  }, [metricItems])

  const handleGroupChange = (_: unknown, value: any) => {
    setSelectedGroupId(value?.id ?? null)
    setSelectedPropertyIds([])
  }

  const handleOpenLayoutOptions = (event: ReactMouseEvent<HTMLElement>) => {
    setLayoutOptionsAnchorEl(event.currentTarget)
  }

  const handleCloseLayoutOptions = () => {
    setLayoutOptionsAnchorEl(null)
  }

  const handleClearSelections = () => {
    setSelectedGroupId(null)
    setSelectedPropertyIds([])
    setExportAllProps(true)
    setTiers([EmissionTier.LOW, EmissionTier.MODERATE, EmissionTier.HIGH])
    setAllPeriods(false)
    setCanonicalPeriods([...canonicalPeriodOptions])
    setLayout(defaultExportLayout)
    setHazardFiltersByHazard({})
    setSelections(s => {
      const next = { ...s }
      Object.keys(next).forEach(k => {
        const key = Number(k)
        next[key] = buildEmptyMetricSelection(key)
      })
      return next
    })
    setSubmitState('idle')
    setSubmitError('')
  }

  const activeHazardFilterMetrics = useMemo(
    () =>
      hazardFilterDialog
        ? metricItems.filter(metric => !metric.hide && metric.hazard === hazardFilterDialog.hazardKey)
        : [],
    [hazardFilterDialog, metricItems],
  )

  return (
    <Page title="Export Climate Data" back size="xl">
      <Box p={1}>
        <Stack spacing={3}>
          {/* <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} spacing={2}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="h6">Configure Export</Typography>
            </Stack>
          </Stack> */}

          {submitState === 'error' && <Alert severity="error">{submitError}</Alert>}
          {submitState === 'success' && <Alert severity="success">Export queued.</Alert>}

          <Accordion variant="outlined" expanded={exportsOpen} onChange={(_, expanded) => setExportsOpen(expanded)}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                <Typography variant="subtitle1" fontWeight={700}>
                  Recent exports
                </Typography>
                {exportCount > 0 && <Chip size="small" label={exportCount} color="primary" />}
                <Box sx={{ flex: 1 }} />
                {metricExportJobsQuery.isFetching && <CircularProgress size={16} />}
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              {metricExportJobsQuery.isLoading && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={18} />
                  <Typography variant="body2">Loading exports…</Typography>
                </Stack>
              )}

              {!metricExportJobsQuery.isLoading && exportCount === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No recent exports yet.
                </Typography>
              )}

              {exportCount > 0 && (
                <Stack spacing={1.25}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Button size="small" color="inherit" onClick={() => metricExportJobsQuery.refetch()}>
                      Refresh
                    </Button>
                    {hasActiveExportJobs && (
                      <Typography variant="body2" color="text.secondary">
                        Updating…
                      </Typography>
                    )}
                  </Stack>
                  {exportsPageJobs.map(job => {
                    const chip = statusChip(job.status)
                    const propertyCount = job.property_ids.length
                    const metricKeyRows = buildMetricKeyDisplayRows(
                      job.metric_keys,
                      metricNameByRef,
                      hazardLabelByHazard,
                    )
                    const metricCount = metricKeyRows.length
                    const metricKeysTooltipContent =
                      metricCount > 0 ? (
                        <Stack spacing={0.5}>
                          {metricKeyRows.map(metricKeyRow => (
                            <Stack
                              key={`${job.id}-${metricKeyRow.key}`}
                              direction="row"
                              spacing={1}
                              alignItems="baseline"
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ minWidth: 96, fontWeight: 700 }}
                              >
                                {metricKeyRow.hazardLabel}
                              </Typography>
                              <Typography variant="caption">{metricKeyRow.metricName}</Typography>
                            </Stack>
                          ))}
                        </Stack>
                      ) : (
                        <Typography variant="caption">No metric key metadata available for this export.</Typography>
                      )
                    return (
                      <Box
                        key={job.id}
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 2,
                          p: 1.25,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.25,
                          flexWrap: 'wrap',
                        }}
                      >
                        <Chip size="small" label={chip.label} color={chip.color} variant={chip.variant} />
                        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`${propertyCount} ${propertyCount === 1 ? 'property' : 'properties'}`}
                          />
                          <Tooltip
                            title={metricKeysTooltipContent}
                            arrow
                            placement="top-end"
                            enterDelay={120}
                            leaveDelay={100}
                            slotProps={{
                              tooltip: {
                                sx: {
                                  maxWidth: 420,
                                  p: 1,
                                },
                              },
                            }}
                          >
                            <Chip
                              size="small"
                              variant="outlined"
                              label={
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                  {`${metricCount} ${metricCount === 1 ? 'metric' : 'metrics'}`}
                                  <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                </span>
                              }
                            />
                          </Tooltip>
                        </Stack>
                        <Typography variant="body2" sx={{ flex: 1, minWidth: 240 }}>
                          Requested {ago(job.created_at)}
                          {job.file_size_bytes ? (
                            <Typography component="span" variant="body2" color="text.secondary">
                              {' '}
                              • {formatBytes(job.file_size_bytes)}
                            </Typography>
                          ) : null}
                        </Typography>
                        {job.status === 'FAILED' && job.error && (
                          <Typography variant="body2" color="error" sx={{ flexBasis: '100%' }}>
                            {job.error}
                          </Typography>
                        )}
                        <Button
                          size="small"
                          variant="contained"
                          disabled={job.status !== 'COMPLETE'}
                          onClick={() => handleDownload(job.id)}
                        >
                          Download
                        </Button>
                      </Box>
                    )
                  })}

                  {exportsPageCount > 1 && (
                    <Stack direction="row" justifyContent="flex-end">
                      <Pagination
                        size="small"
                        count={exportsPageCount}
                        page={exportsPageSafe}
                        onChange={(_, page) => setExportsPage(page)}
                      />
                    </Stack>
                  )}
                </Stack>
              )}
            </AccordionDetails>
          </Accordion>

          <Card variant="outlined">
            <Box
              sx={{ px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider', backgroundColor: 'grey.50' }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                justifyContent="space-between"
              >
                <Typography variant="subtitle1" fontWeight={700}>
                  Scope
                </Typography>
              </Stack>
            </Box>
            <CardContent>
              <Stack spacing={2.5}>
                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      Properties
                    </Typography>
                    <Chip
                      size="small"
                      label={`${selectedPropertyCount} selected`}
                      color={selectedPropertyCount ? 'primary' : 'default'}
                      variant={selectedPropertyCount ? 'filled' : 'outlined'}
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Choose the portfolio slice first, then decide whether to export everything in scope or only a
                    smaller subset.
                  </Typography>
                  {scopeLoading && (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                      <CircularProgress size={18} />
                      <Typography variant="body2">Loading properties…</Typography>
                    </Stack>
                  )}
                  <Stack spacing={1.5}>
                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 2,
                        px: 1.5,
                        py: 1.25,
                        backgroundColor: 'grey.50',
                      }}
                    >
                      <Typography variant="body1" fontWeight={600} sx={{ mb: 0.25 }}>
                        Property group
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {selectedGroup
                          ? `Currently scoped to ${selectedGroup.name}. Leave the group blank to switch back to the full portfolio.`
                          : 'No group selected, so all available properties will be used.'}
                      </Typography>
                    </Box>
                    <Autocomplete
                      options={locationGroupItems}
                      getOptionLabel={o => o.name}
                      value={selectedGroup}
                      onChange={handleGroupChange}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      renderInput={params => <TextField {...params} label="Property group" placeholder="All" />}
                      renderOption={(props, option) => (
                        <Box component="li" {...props} key={option.id} sx={{ display: 'flex', width: '100%' }}>
                          <Typography variant="body1" sx={{ flex: 1 }}>
                            {option.name}
                          </Typography>
                          {option.property_customer_reference && (
                            <Typography variant="caption" color="text.secondary">
                              {option.property_customer_reference}
                            </Typography>
                          )}
                        </Box>
                      )}
                    />
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} useFlexGap>
                      <Box
                        onClick={() => setExportAllProps(true)}
                        sx={{
                          flex: '1 1 240px',
                          minWidth: { xs: '100%', md: 240 },
                          border: '1px solid',
                          borderColor: exportAllProps ? 'primary.main' : 'divider',
                          backgroundColor: exportAllProps ? 'action.selected' : 'transparent',
                          borderRadius: 2,
                          px: 1.5,
                          py: 1.25,
                          cursor: 'pointer',
                        }}
                      >
                        <Stack spacing={0.5}>
                          <Typography variant="body1" fontWeight={exportAllProps ? 700 : 500}>
                            All properties in scope
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {selectedGroup
                              ? `Export every property from ${selectedGroup.name}.`
                              : 'Export every property in the portfolio.'}
                          </Typography>
                        </Stack>
                      </Box>
                      <Box
                        onClick={() => setExportAllProps(false)}
                        sx={{
                          flex: '1 1 240px',
                          minWidth: { xs: '100%', md: 240 },
                          border: '1px solid',
                          borderColor: !exportAllProps ? 'primary.main' : 'divider',
                          backgroundColor: !exportAllProps ? 'action.selected' : 'transparent',
                          borderRadius: 2,
                          px: 1.5,
                          py: 1.25,
                          cursor: 'pointer',
                        }}
                      >
                        <Stack spacing={0.5}>
                          <Typography variant="body1" fontWeight={!exportAllProps ? 700 : 500}>
                            Specific properties
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Choose a smaller set manually for this export.
                          </Typography>
                        </Stack>
                      </Box>
                    </Stack>
                    {exportAllProps ? (
                      <></>
                    ) : (
                      <Autocomplete
                        multiple
                        fullWidth
                        disableCloseOnSelect
                        options={locationItems}
                        getOptionLabel={(o: any) => o.name}
                        isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                        value={selectedLocationItems}
                        filterOptions={filterPropertyOptions}
                        onInputChange={(_, newValue, reason) => {
                          // Update the ref synchronously so filterOptions always
                          // reads the correct text on the very next render,
                          // regardless of MUI's internal blur/reset side-effects.
                          if (reason === 'input') {
                            propertySearchRef.current = newValue
                          } else if (reason === 'clear') {
                            propertySearchRef.current = ''
                          }
                          // 'reset' events (blur, value changes from polling, post-selection)
                          // are intentionally ignored — the ref keeps its value until
                          // the user explicitly types or clears.
                        }}
                        onChange={(_, vals) => {
                          setSelectedPropertyIds((vals as any[]).map(v => v.id))
                          // Clear the filter so the next search starts fresh.
                          propertySearchRef.current = ''
                        }}
                        renderInput={params => (
                          <TextField {...params} label="Specific properties" placeholder="Select properties" />
                        )}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => (
                            <Chip variant="outlined" label={option.name} {...getTagProps({ index })} key={option.id} />
                          ))
                        }
                        renderOption={(props, option) => (
                          <Box component="li" {...props} key={option.id}>
                            {option.name}
                          </Box>
                        )}
                      />
                    )}
                    {!exportAllProps && selectedPropertyIds.length === 0 && (
                      <Typography variant="body2" color="text.secondary">
                        Select one or more properties to narrow the export scope.
                      </Typography>
                    )}
                  </Stack>
                </Box>

                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
                    Emission tiers
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Filter by scenario tier. The export still keeps the underlying scenario visible on each row.
                  </Typography>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
                    {emissionTierOptions.map(option => {
                      const selected = tiers.includes(option.name)
                      const meta = emissionTierExportMeta[option.name]
                      return (
                        <Box
                          key={option.name}
                          onClick={() => toggleTier(option.name)}
                          sx={{
                            flex: '1 1 210px',
                            minWidth: { xs: '100%', md: 210 },
                            border: '1px solid',
                            borderColor: selected ? 'primary.main' : 'divider',
                            backgroundColor: selected ? 'action.selected' : 'transparent',
                            borderRadius: 2,
                            px: 1.5,
                            py: 1.25,
                            cursor: 'pointer',
                          }}
                        >
                          <Stack spacing={0.5}>
                            <Stack
                              direction={{ xs: 'column', sm: 'row' }}
                              spacing={0.75}
                              alignItems={{ xs: 'flex-start', sm: 'center' }}
                              justifyContent="space-between"
                            >
                              <Typography variant="body1" fontWeight={selected ? 700 : 500}>
                                {meta.title}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                {meta.detail}
                              </Typography>
                            </Stack>
                            <Typography variant="body2" color="text.secondary">
                              {meta.scenarios}
                            </Typography>
                          </Stack>
                        </Box>
                      )
                    })}
                  </Stack>
                </Box>

                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      Periods
                    </Typography>
                    {/* <Chip
                      size="small"
                      label={allPeriods ? 'All periods' : `${canonicalPeriods.length} selected`}
                      color={allPeriods || canonicalPeriods.length ? 'primary' : 'default'}
                      variant={allPeriods || canonicalPeriods.length ? 'filled' : 'outlined'}
                    /> */}
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Choose one period group: either include every available underlying period returned by the data, or
                    reduce the export to the three standard tagged terms below for easier comparison.
                  </Typography>
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={1.25}
                    alignItems={{ xs: 'stretch', md: 'stretch' }}
                    useFlexGap
                  >
                    <Box
                      onClick={() => {
                        if (allPeriods) {
                          setAllPeriods(false)
                          setCanonicalPeriods([...canonicalPeriodOptions])
                          return
                        }
                        setAllPeriods(true)
                        setCanonicalPeriods([])
                      }}
                      sx={{
                        flex: { xs: '1 1 auto', md: '0 0 240px' },
                        minWidth: { xs: '100%', md: 240 },
                        border: '1px solid',
                        borderColor: allPeriods ? 'primary.main' : 'divider',
                        backgroundColor: allPeriods ? 'action.selected' : 'transparent',
                        borderRadius: 2,
                        px: 1.5,
                        py: 1.25,
                        cursor: 'pointer',
                      }}
                    >
                      <Stack spacing={0.5}>
                        <Typography variant="body1" fontWeight={allPeriods ? 700 : 500}>
                          All available periods
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Include every available underlying period. This can return more granular periods than the
                          three standard terms.
                        </Typography>
                      </Stack>
                    </Box>
                    <Box
                      sx={{
                        display: { xs: 'none', md: 'flex' },
                        alignItems: 'stretch',
                        justifyContent: 'center',
                        px: 0.25,
                      }}
                    >
                      <Divider orientation="vertical" flexItem />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack
                        direction="row"
                        spacing={1}
                        // alignItems="center"
                        // justifyContent="space-between"
                        sx={{ mb: 1 }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          Standard terms only
                        </Typography>
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => {
                            setAllPeriods(false)
                            setCanonicalPeriods([...canonicalPeriodOptions])
                          }}
                          sx={{
                            minWidth: 0,
                            px: 0,
                            py: 0,
                            fontSize: theme => theme.typography.caption.fontSize,
                            textTransform: 'none',
                          }}
                        >
                          select all
                        </Button>
                      </Stack>
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
                        {canonicalPeriodOptions.map(period => {
                          const selected = !allPeriods && canonicalPeriods.includes(period)
                          const meta = canonicalPeriodMeta[period]
                          return (
                            <Box
                              key={period}
                              onClick={() => {
                                if (allPeriods) {
                                  setAllPeriods(false)
                                  setCanonicalPeriods([period])
                                  return
                                }
                                toggleCanonicalPeriod(period)
                              }}
                              sx={{
                                flex: '1 1 210px',
                                minWidth: { xs: '100%', md: 210 },
                                border: '1px solid',
                                borderColor: selected ? 'primary.main' : 'divider',
                                backgroundColor: selected ? 'action.selected' : 'transparent',
                                borderRadius: 2,
                                px: 1.5,
                                py: 1.25,
                                cursor: 'pointer',
                              }}
                            >
                              <Stack spacing={0.5}>
                                <Stack
                                  direction={{ xs: 'column', sm: 'row' }}
                                  spacing={0.75}
                                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                                  justifyContent="space-between"
                                >
                                  <Typography variant="body1" fontWeight={selected ? 700 : 500}>
                                    {meta.title}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                    {meta.detail}
                                  </Typography>
                                </Stack>
                                <Typography variant="body2" color="text.secondary">
                                  {meta.description}
                                </Typography>
                              </Stack>
                            </Box>
                          )
                        })}
                      </Stack>
                    </Box>
                  </Stack>
                  {!allPeriods && canonicalPeriods.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1.25 }}>
                      Select one or more standard terms to narrow the export to the tagged planning horizons.
                    </Typography>
                  )}
                </Box>
              </Stack>
            </CardContent>
          </Card>

          <Card variant="outlined">
            <Box
              sx={{ px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider', backgroundColor: 'grey.50' }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                justifyContent="space-between"
              >
                <Typography variant="subtitle1" fontWeight={700}>
                  Layout
                </Typography>
              </Stack>
            </Box>
            <CardContent>
              <Stack spacing={2.5}>
                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
                    Workbook format
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Choose whether to include the raw row export, the analysis-friendly wide export, or both.
                  </Typography>
                  <FormControl fullWidth>
                    <InputLabel id="layout-mode-label">Workbook format</InputLabel>
                    <Select
                      labelId="layout-mode-label"
                      label="Workbook format"
                      value={layout.mode}
                      onChange={event =>
                        setLayout(prev => ({
                          ...prev,
                          mode: event.target.value as ExportLayoutMode,
                        }))
                      }
                    >
                      {exportLayoutModeOptions.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                          <Stack spacing={0.25}>
                            <Typography variant="body2">{option.label}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {option.description}
                            </Typography>
                          </Stack>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {/* <Typography variant="body2" color="text.secondary" sx={{ mt: 1.25 }}>
                    {exportLayoutModeOptions.find(option => option.value === layout.mode)?.description}
                  </Typography> */}
                </Box>

                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    p: 2,
                    opacity: includesWideSheets ? 1 : 0.72,
                  }}
                >
                  <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
                    Analysis sheets
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Configure how the wide analysis export is organised. These settings apply only when analysis sheets
                    are included.
                  </Typography>
                  <Stack spacing={1.5}>
                    <FormControl fullWidth disabled={!includesWideSheets}>
                      <InputLabel id="layout-sheet-partitioning-label">Sheet structure</InputLabel>
                      <Select
                        labelId="layout-sheet-partitioning-label"
                        label="Sheet structure"
                        value={layout.sheet_partitioning}
                        onChange={event =>
                          setLayout(prev => ({
                            ...prev,
                            sheet_partitioning: event.target.value as ExportSheetPartitioning,
                          }))
                        }
                      >
                        {exportSheetPartitioningOptions.map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            <Stack spacing={0.25}>
                              <Typography variant="body2">{option.label}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {option.description}
                              </Typography>
                            </Stack>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1}
                      alignItems={{ xs: 'flex-start', sm: 'center' }}
                      justifyContent="space-between"
                    >
                      <Typography variant="body2" color="text.secondary">
                        {layoutOptionsSummary}
                      </Typography>
                      <Button
                        variant="text"
                        size="small"
                        color="inherit"
                        startIcon={<TuneOutlinedIcon />}
                        disabled={!includesWideSheets}
                        onClick={handleOpenLayoutOptions}
                        sx={{
                          px: 1.25,
                          borderRadius: 999,
                          color: 'text.secondary',
                          backgroundColor: 'action.hover',
                          '&:hover': {
                            backgroundColor: 'action.selected',
                          },
                        }}
                      >
                        More options
                      </Button>
                    </Stack>
                    <Popover
                      open={layoutOptionsOpen}
                      anchorEl={layoutOptionsAnchorEl}
                      onClose={handleCloseLayoutOptions}
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                      PaperProps={{
                        sx: {
                          width: 320,
                          maxWidth: 'calc(100vw - 32px)',
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 2,
                          boxShadow: theme => theme.shadows[4],
                        },
                      }}
                    >
                      <Stack spacing={1.25} sx={{ p: 1.5 }}>
                        <Box
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2,
                            px: 1.25,
                            py: 1,
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Switch
                                checked={layout.short_metric_names}
                                onChange={(_, checked) =>
                                  setLayout(prev => ({
                                    ...prev,
                                    short_metric_names: checked,
                                  }))
                                }
                              />
                            }
                            label="Short metric names"
                          />
                          <Typography variant="body2" color="text.secondary">
                            Use short metric labels in wide-sheet headers when the catalogue provides them.
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2,
                            px: 1.25,
                            py: 1,
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Switch
                                checked={layout.compact_headers}
                                onChange={(_, checked) =>
                                  setLayout(prev => ({
                                    ...prev,
                                    compact_headers: checked,
                                  }))
                                }
                              />
                            }
                            label="Compact analysis headers"
                          />
                          <Typography variant="body2" color="text.secondary">
                            Hide repeated context from wide-sheet headers when the sheet name already provides it.
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2,
                            px: 1.25,
                            py: 1,
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Switch
                                checked={layout.include_column_dictionary}
                                onChange={(_, checked) =>
                                  setLayout(prev => ({
                                    ...prev,
                                    include_column_dictionary: checked,
                                  }))
                                }
                              />
                            }
                            label="Include column dictionary"
                          />
                          <Typography variant="body2" color="text.secondary">
                            Add a reference sheet describing each wide-sheet column and its metric context.
                          </Typography>
                        </Box>
                      </Stack>
                    </Popover>
                  </Stack>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          <Card variant="outlined">
            <Box
              sx={{ px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider', backgroundColor: 'grey.50' }}
            >
              <Stack
                direction={{ xs: 'column', lg: 'row' }}
                spacing={1}
                alignItems={{ xs: 'stretch', lg: 'center' }}
                justifyContent="space-between"
              >
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Metrics
                  </Typography>
                </Stack>
                <MetricExportProfilePanel
                  ref={metricExportProfilePanelRef}
                  inline
                  metrics={metricItems}
                  selections={selections}
                  hazardFilters={hazardFiltersByHazard}
                  onSelectionsChange={setSelections}
                  onHazardFiltersChange={setHazardFiltersByHazard}
                  onCompatibilityChange={handleProfileCompatibilityChange}
                />
              </Stack>
            </Box>

            <CardContent sx={{ p: 0 }}>
              <>
                {metricsLoading && (
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 2, py: 2 }}>
                    <CircularProgress size={20} />
                    <Typography variant="body2">Loading hazards & metrics…</Typography>
                  </Stack>
                )}

                {!metricsLoading && (hzError || mError) && (
                  <Alert severity="error" sx={{ borderRadius: 0 }}>
                    Failed to load hazards or metrics.
                  </Alert>
                )}

                {!metricsLoading && !hazards?.length && !mError && (
                  <Alert severity="info" sx={{ borderRadius: 0 }}>
                    No climate hazards or metrics available.
                  </Alert>
                )}

                {!metricsLoading &&
                  !hzError &&
                  !mError &&
                  orderedHazardGroups.map(([hazardKey, groups]) => {
                    const hazard = hazardLookup.get(hazardKey)
                    const hazardLabel = hazard?.display_name || hazardKey
                    const hazardDescription = hazard?.description
                    const selectedCount = selectedMetricCountByHazard.get(hazardKey) ?? 0
                    const hazardMetrics = groups.flatMap(group => group.metrics)
                    const hazardFilters = hazardFiltersByHazard[hazardKey] ?? null
                    const hasHazardConfig = hasMetricDataFilters(hazardFilters)

                    return (
                      <Accordion
                        key={hazardKey}
                        disableGutters
                        square
                        elevation={0}
                        sx={{
                          '&:before': { display: 'none' },
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                        }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          sx={{ px: 1.25, minHeight: 44, '& .MuiAccordionSummary-content': { my: 0.25 } }}
                        >
                          <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                                <Typography
                                  variant="subtitle1"
                                  sx={{
                                    fontWeight: 700,
                                    letterSpacing: 0.15,
                                    lineHeight: 1.2,
                                  }}
                                >
                                  {hazardLabel}
                                </Typography>
                                {hazardDescription ? (
                                  <IconButton
                                    size="small"
                                    aria-label={`Hazard description for ${hazardLabel}`}
                                    sx={{ p: 0.25, color: 'text.secondary' }}
                                    onClick={e => {
                                      e.preventDefault()
                                      e.stopPropagation()
                                      setHazardDescriptionDialog({
                                        title: hazardLabel,
                                        description: hazardDescription,
                                      })
                                    }}
                                  >
                                    <InfoOutlinedIcon fontSize="small" />
                                  </IconButton>
                                ) : null}
                                <FilterConfigButton
                                  active={hasHazardConfig}
                                  title={hasHazardConfig ? 'Edit hazard filters' : 'Configure hazard filters'}
                                  ariaLabel={`Configure ${hazardLabel} filters`}
                                  onClick={event => {
                                    event.preventDefault()
                                    event.stopPropagation()
                                    setHazardFilterDialog({
                                      hazardKey,
                                      hazardLabel,
                                    })
                                  }}
                                />
                              </Stack>
                            </Box>
                            {(incompatibleMetricCountByHazard.get(hazardKey) ?? 0) > 0 ? (
                              <Chip
                                size="small"
                                color="error"
                                variant="outlined"
                                label={`${incompatibleMetricCountByHazard.get(hazardKey)} incompatible`}
                              />
                            ) : null}
                            {selectedCount > 0 ? <Chip size="small" color="primary" label={selectedCount} /> : null}
                          </Stack>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 0 }}>
                          <Stack spacing={0.5}>
                            {groups.map(group => (
                              <Box key={`${hazardKey}-${group.id}`}>
                                <Box
                                  sx={{
                                    px: 1,
                                    py: 0.75,
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                    backgroundColor: 'grey.100',
                                  }}
                                >
                                  <Typography variant="subtitle2" color="text.secondary">
                                    {group.label}
                                  </Typography>
                                </Box>
                                <Box>
                                  {group.metrics.map(metric => (
                                    <MetricRow
                                      key={metric.id}
                                      metric={metric}
                                      hazardLabel={hazardLabel}
                                      selection={selections[metric.id] || buildEmptyMetricSelection(metric.id)}
                                      isProfileIncompatible={incompatibleProfileMetricIds.has(metric.id)}
                                      onResetProfileMetric={() =>
                                        metricExportProfilePanelRef.current?.resetIncompatibleMetric(metric.id)
                                      }
                                      onChange={next => handleSelectionChange(metric.id, next)}
                                    />
                                  ))}
                                </Box>
                              </Box>
                            ))}
                          </Stack>
                        </AccordionDetails>
                      </Accordion>
                    )
                  })}
              </>
            </CardContent>
          </Card>

          <HazardFilterDialog
            open={hazardFilterDialog !== null}
            hazardLabel={hazardFilterDialog?.hazardLabel ?? 'Hazard'}
            metrics={activeHazardFilterMetrics}
            initialFilters={hazardFilterDialog ? hazardFiltersByHazard[hazardFilterDialog.hazardKey] ?? null : null}
            onClose={() => setHazardFilterDialog(null)}
            onSave={filters => {
              if (!hazardFilterDialog) return
              handleHazardFilterSave(hazardFilterDialog.hazardKey, filters)
            }}
          />

          <Dialog
            open={hazardDescriptionDialog !== null}
            onClose={() => setHazardDescriptionDialog(null)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>{hazardDescriptionDialog?.title}</DialogTitle>
            <DialogContent>
              <Typography variant="body2" color="text.secondary">
                {hazardDescriptionDialog?.description}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setHazardDescriptionDialog(null)}>Close</Button>
            </DialogActions>
          </Dialog>

          <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="flex-start">
            <Button variant="contained" onClick={handleExport} disabled={exportDisabled} sx={{ minWidth: 120 }}>
              {submitState === 'submitting' ? 'Exporting…' : 'Export'}
            </Button>
            <Button variant="text" onClick={handleClearSelections}>
              Clear selection
            </Button>
            {/* <Chip
              label={`${selectedMetricCount} metric${selectedMetricCount === 1 ? '' : 's'} selected`}
              color={selectedMetricCount ? 'primary' : 'default'}
              variant={selectedMetricCount ? 'filled' : 'outlined'}
            /> */}
          </Stack>
        </Stack>
      </Box>
    </Page>
  )
}

export default MetricsExportPage
